import { observable, computed, batch } from "@legendapp/state";
import type ReactPlayer from "react-player";
import type { RefObject } from "react";
import type { GetLessonDataResponse } from "@shared_types";
import type { VideoActivity, UserVideoWord, UserWordStatus } from "../types";
import type { Activity } from "@/activities";
import type { PanelMode } from "../types";
import { feedCurrentIndex$, chapters$ } from "./feedState";

// ---------------------------------------------------------------------------
// Lesson navigation
// ---------------------------------------------------------------------------

export const lesson$ = observable({ currentLessonIndex: 0 });
export const activeWordIndex$ = observable(0);
export const seekPending$ = observable(false);

// ---------------------------------------------------------------------------
// Playback control
// ---------------------------------------------------------------------------

export const isPaused$ = observable(false);
export const seekRequestMs$ = observable<number | null>(null);
export const videoPlayed$ = observable(0);
export const videoLoaded$ = observable(0);
export const videoDuration$ = observable(0);
export const playerRef: RefObject<ReactPlayer | null> = { current: null };
export const scrubberSeekFraction$ = observable<number | null>(null);
/** True while the user is dragging the scrubber — lets the transcript follow the
 *  scrub position even though the video is effectively paused mid-drag. */
export const scrubbing$ = observable(false);

// ---------------------------------------------------------------------------
// Lesson data cache (keyed by lessonId) -- stores full GetLessonDataResponse
// ---------------------------------------------------------------------------

export const lessonDataCache$ = observable<Record<number, GetLessonDataResponse | null>>({});

/**
 * Client-GENERATED activities, keyed by lessonId. Mirrors iOS: the feed builds
 * a mixed set (MCQ / Match / Write / Fill) on-device from the lesson's words,
 * not the backend `guess`.
 * `undefined` = not yet attempted, `null` = generated but none possible,
 * a non-empty array = the activities to show in order.
 */
export const generatedActivities$ = observable<Record<number, Activity[] | null>>({});

/**
 * Furthest clip-relative position (ms past the lesson's videoStart) the user
 * has reached for each lesson, keyed by lessonId. Mirrors iOS
 * `maxPositionReachedRef`: it accumulates from BOTH linear watch-time deltas
 * (handleProgress) AND word-seek jumps (transcript tap), and only ever moves
 * forward. The 55%/90% activity-generation thresholds are evaluated against
 * this — NOT against YouTube's whole-video played fraction (which can read
 * negative before the start-seek lands or exceed 1).
 */
export const maxPositionReachedMs$ = observable<Record<number, number>>({});

/**
 * Advance a lesson's max-reached clip position to `positionMs` (clip-relative,
 * ms past videoStart) if it's further than what we've already recorded. No-op
 * for non-positive / backwards moves. Mirrors iOS's `if (pos > ref) ref = pos`.
 */
export function advanceMaxPositionReached(
  lessonId: number,
  positionMs: number,
): void {
  if (positionMs <= 0) return;
  const current = maxPositionReachedMs$[lessonId].peek() ?? 0;
  if (positionMs > current) {
    maxPositionReachedMs$[lessonId].set(positionMs);
  }
}

/** Add a watch-time delta (ms) to a lesson's max-reached clip position. */
export function addWatchTimeToMaxPosition(
  lessonId: number,
  deltaMs: number,
): void {
  if (deltaMs <= 0) return;
  const current = maxPositionReachedMs$[lessonId].peek() ?? 0;
  maxPositionReachedMs$[lessonId].set(current + deltaMs);
}

/**
 * Per-lesson set of activity TYPES the user has already completed this session,
 * keyed by lessonId. Mirrors iOS feedStore$.lessonActivityState[lessonId]
 * .completedActivities — used to avoid re-awarding drops / re-firing the
 * completion analytics when an already-completed activity is re-entered.
 */
export const completedActivities$ = observable<Record<number, Set<string>>>({});

/** Returns VideoActivity for the active lesson in the current chapter, or null. */
export const activeLessonData$ = computed<VideoActivity | null>(() => {
  const chapter = chapters$.get()[feedCurrentIndex$.get()];
  if (!chapter) return null;
  const lessonId = chapter.lessonIDs[lesson$.currentLessonIndex.get()];
  if (!lessonId) return null;
  return lessonDataCache$[lessonId]?.get()?.video ?? null;
});

// ---------------------------------------------------------------------------
// Captions / panel UI
// ---------------------------------------------------------------------------

export const captionsVisible$ = observable(true);
/** Whether the wordlist bulk-mark modal is open (flag-gated: `wordlist_in_feed`). */
export const wordlistModalOpen$ = observable(false);
/** Whether the current lesson's activity (quiz) panel is showing instead of its video. */
export const activityVisible$ = observable(false);
export const selectedWord$ = observable<UserVideoWord | null>(null);
/**
 * Optimistic local overrides for word status, keyed by the canonical `wordKey`
 * (NOT the per-occurrence `videoWordId`). Keying by wordKey means a status change
 * on one occurrence of a word instantly reflects on EVERY other occurrence of
 * that same word — both elsewhere in the same transcript and in later lessons
 * that contain it — mirroring iOS, where status lives in the global word store
 * keyed by wordKey. Synced to the backend on change (putWordStatus uses wordKey).
 */
export const wordStatusOverrides$ = observable<Record<string, UserWordStatus>>({});

// wordKeys are content-derived, so a word can be literally "get", "set",
// "delete", etc. Indexing the Legend-State proxy with such a key
// (`wordStatusOverrides$["get"]`) returns the observable's own `get` METHOD
// instead of a child node, so a following `.get()`/`.set()`/`.peek()` throws
// ("get is not a function") and crashes the whole transcript. These accessors
// read/write the record as a plain object instead, which is immune to that
// method-name collision. The own-property check matters too: a wordKey like
// "constructor" or "toString" would otherwise resolve to an Object.prototype
// member (a function, so `?? word.status` wouldn't catch it) rather than a
// missing override. Reads subscribe to the whole record, but the transcript has
// only tens of words so the extra re-render breadth is negligible.
const readOverride = (
  record: Record<string, UserWordStatus>,
  wordKey: string,
): UserWordStatus | undefined =>
  Object.prototype.hasOwnProperty.call(record, wordKey)
    ? record[wordKey]
    : undefined;

/** Reactive read of a word's override status (subscribes to the whole record). */
export function readWordOverride(
  wordKey: string,
): UserWordStatus | undefined {
  return readOverride(wordStatusOverrides$.get(), wordKey);
}

/** Non-reactive read of a word's override status. */
export function peekWordOverride(
  wordKey: string,
): UserWordStatus | undefined {
  return readOverride(wordStatusOverrides$.peek(), wordKey);
}

/** Set a single word's override status without clobbering the other keys. */
export function setWordOverride(
  wordKey: string,
  status: UserWordStatus,
): void {
  wordStatusOverrides$.assign({ [wordKey]: status });
}

export const isDragSelecting$ = observable(false);
export const dragHighlight$ = observable<[number, number] | null>(null);
export const selectedRange$ = observable<[number, number] | null>(null);
export const selectionFinalized$ = observable(false);
/**
 * Viewport anchor for the translation bubble: the center-x and top-y of the
 * tapped word (or the union box of a phrase selection). The bubble positions
 * itself ABOVE this point with a downward pointer (iOS SpeechBubblePopup parity).
 * It's always set fresh when a word/phrase is selected (and only read while a
 * panel is open), so the clear functions deliberately DON'T null it — clearing
 * it here would blank the bubble's position on a touch-cancel that fires while a
 * word panel is still open, leaving the backdrop blocking the UI.
 */
export const popupAnchor$ = observable<{
  cx: number;
  top: number;
  /** Horizontal bounds of the video column, so the translation bubble stays
   *  within the video (not the full screen) on wide desktops. */
  left: number;
  right: number;
} | null>(null);

/**
 * Bumped each time a phrase is SAVED, to trigger the app-level "saved" toast
 * independently of the popup's lifecycle. The popup (and its outside-tap
 * backdrop) unmount on close, so a toast rendered inside it would vanish
 * immediately; SavedPhraseToast watches this counter from a stable mount point.
 */
export const savedPhraseToast$ = observable(0);

/**
 * Bumped each time a word is marked KNOWN (the checkmark in the word panel), to
 * trigger the app-level "added to known" toast independently of the panel's
 * lifecycle — same pattern as savedPhraseToast$.
 */
export const wordKnownToast$ = observable(0);

export function clearSelection(): void {
  selectedRange$.set(null);
  selectionFinalized$.set(false);
}

export function clearWordTranslation(): void {
  selectedWord$.set(null);
}

// ---------------------------------------------------------------------------
// Active word tracking (timing-based, no text)
// ---------------------------------------------------------------------------

export function updateActiveWord(currentTimeMs: number): void {
  const videoActivity = activeLessonData$.get();
  if (!videoActivity) return;
  const words = videoActivity.userVideoWords;
  if (!words || words.length === 0) return;

  const idx = activeWordIndex$.get();
  const currentWord = words[idx];

  if (currentWord) {
    const effectiveDuration = currentWord.duration > 0 ? currentWord.duration : 1;
    const wordEnd = currentWord.start + effectiveDuration;
    if (currentTimeMs >= currentWord.start && currentTimeMs < wordEnd) return;
  }

  let newIdx = 0;
  for (let i = 0; i < words.length; i++) {
    if (currentTimeMs >= words[i].start) {
      newIdx = i;
    } else {
      break;
    }
  }

  if (newIdx !== idx) {
    activeWordIndex$.set(newIdx);
  }
}

// ---------------------------------------------------------------------------
// Player lifecycle
// ---------------------------------------------------------------------------

export function resetPlayerState(): void {
  batch(() => {
    seekPending$.set(true);
    activeWordIndex$.set(0);
    clearWordTranslation();
    isPaused$.set(false);
    videoPlayed$.set(0);
    videoLoaded$.set(0);
    scrubberSeekFraction$.set(null);
    scrubbing$.set(false);
    seekRequestMs$.set(null);
    isDragSelecting$.set(false);
    dragHighlight$.set(null);
    activityVisible$.set(false);
    clearSelection();
  });
}

/**
 * Reset a lesson's max-reached clip position so its 55%/90% triggers re-arm
 * when the lesson is re-entered (iOS recreates the ref on remount).
 */
export function resetMaxPositionReached(lessonId: number): void {
  maxPositionReachedMs$[lessonId].set(0);
}

export function nextLesson(totalLessons: number): void {
  lesson$.currentLessonIndex.set((i) => Math.min(totalLessons - 1, i + 1));
  resetPlayerState();
}

export function previousLesson(): void {
  if (lesson$.currentLessonIndex.get() <= 0) return;
  lesson$.currentLessonIndex.set((i) => Math.max(0, i - 1));
  resetPlayerState();
}

// ---------------------------------------------------------------------------
// Bottom panel state
// ---------------------------------------------------------------------------

export const panelMode$ = computed<PanelMode | null>(() => {
  const word = selectedWord$.get();
  const range = selectedRange$.get();
  const isFinalized = range && range[0] !== range[1] && selectionFinalized$.get();
  if (word) return "word";
  if (isFinalized) return "sentence";
  return null;
});

/** Word-by-word English gloss of the current phrase selection (for the sentence panel). */
export const selectedPhraseGloss$ = computed<string>(() => {
  const range = selectedRange$.get();
  const activity = activeLessonData$.get();
  if (!range || !activity?.userVideoWords) return "";
  const [a, b] = range[0] <= range[1] ? range : [range[1], range[0]];
  return activity.userVideoWords
    .slice(a, b + 1)
    .map((w) => w.definition.nativeWord)
    .filter(Boolean)
    .join(" ");
});

/**
 * The current phrase selection's metadata for the server phrase-translate +
 * save flow (`POST /auth/phrase`, `PUT /auth/phrase/saved`): the bounding
 * videoWordIds and the Spanish surface text.
 */
export interface SelectedPhraseInfo {
  startVideoWordId: number;
  endVideoWordId: number;
  text: string;
}

export const selectedPhraseInfo$ = computed<SelectedPhraseInfo | null>(() => {
  const range = selectedRange$.get();
  const activity = activeLessonData$.get();
  if (!range || !activity?.userVideoWords) return null;
  const [a, b] = range[0] <= range[1] ? range : [range[1], range[0]];
  const words = activity.userVideoWords.slice(a, b + 1);
  if (words.length === 0) return null;
  return {
    startVideoWordId: words[0].videoWordId,
    endVideoWordId: words[words.length - 1].videoWordId,
    text: words.map((w) => w.displayText).join(" "),
  };
});

export function handlePanelClose(): void {
  const mode = panelMode$.get();
  if (mode === "word") {
    clearWordTranslation();
    isPaused$.set(false);
  } else {
    clearSelection();
    isPaused$.set(false);
  }
}
